/**
 * POITableContext - Shared state management for POI tables
 *
 * Provides shared state across all POI table instances:
 * - AssetsSearchPage (full page)
 * - AutodeskViewer (sidebar)
 * - WebIfcViewer (sidebar)
 * - SitesPage (modal)
 *
 * State persists when navigating between pages (within same site).
 * State RESETS when switching to a different site.
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef, ReactNode } from 'react';
import { createAssetFacetReader, matchesAssetChoices, normalizeAssetChoices, toggleAssetPageSelection, type AssetFilterChoices, type AssetFacetReader } from '../components/UnifiedPOITable/assetFilters';
import { apiService } from '../services/ApiService';
import { aggregatePOIRows, computeRoomAssignmentStats, RoomAssignmentStats } from '../utils/poiRowAggregation';
import {
  FilterCondition,
  FilterGroup,
  ColumnConfig,
  SortConfig,
  DEFAULT_COLUMNS,
  PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  RoomOption
} from '../types/poi-table';

// ============================================================================
// State Persistence (sessionStorage)
// ============================================================================

const STORAGE_KEY_PREFIX = 'mabat3d_poi_table_state_';

interface PersistedState {
  assetChoices?: AssetFilterChoices;
  selectedPOIs: string[];  // Set converted to array for JSON
  searchTerm: string;
  filterGroups: FilterGroup[];
  sortConfig: SortConfig;
  columns: ColumnConfig[];
}

const loadPersistedState = (siteId: string): PersistedState | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY_PREFIX + siteId);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const savePersistedState = (siteId: string, state: PersistedState) => {
  try {
    sessionStorage.setItem(STORAGE_KEY_PREFIX + siteId, JSON.stringify(state));
  } catch {
    // Storage full or unavailable - silently fail
  }
};

// ============================================================================
// Utility Functions (extracted from AssetsSearchPage)
// ============================================================================

// Field extraction helpers moved to utils/POIFieldExtractor (DUP-16).
// Re-exported here so existing consumers keep importing from this module.
import {
  parseCustomData,
  getPOIElementDbId,
  getPOISourceFile,
  getFieldValue
} from '../utils/POIFieldExtractor';

export { parseCustomData, getPOIElementDbId, getPOISourceFile, getFieldValue };

import {
  isPOIIfcLoaded as isPOIIfcLoadedDeep,
  isPOIIfcLoading as isPOIIfcLoadingDeep
} from '../utils/POIFileUtils';

// Check if POI matches a filter condition
export const matchesCondition = (poi: any, condition: FilterCondition): boolean => {
  const value = String(getFieldValue(poi, condition.field) || '').toLowerCase();
  const filterValue = condition.value.toLowerCase();

  switch (condition.operator) {
    case 'equals':
      return value === filterValue;
    case 'contains':
      return value.includes(filterValue);
    case 'startsWith':
      return value.startsWith(filterValue);
    case 'endsWith':
      return value.endsWith(filterValue);
    case 'notEquals':
      return value !== filterValue;
    case 'isEmpty':
      return value === '';
    case 'isNotEmpty':
      return value !== '';
    default:
      return true;
  }
};

// Check if POI matches all filter groups
export const matchesFilterGroups = (poi: any, filterGroups: FilterGroup[]): boolean => {
  if (filterGroups.length === 0) return true;

  // All groups must match (AND between groups)
  return filterGroups.every(group => {
    if (group.conditions.length === 0) return true;

    if (group.logic === 'AND') {
      return group.conditions.every(c => matchesCondition(poi, c));
    } else {
      return group.conditions.some(c => matchesCondition(poi, c));
    }
  });
};

// Check if POI's model is loaded / loading — delegate to THE deep implementation
// (utils/POIFileUtils, E-12b). The shallow copies that lived here only consulted
// getPOISourceFile, so the same POI could show a 3D affordance in one table and
// not another. The deep check is now a strict superset (rvt_source_file included).
export const isPOILoaded = (poi: any, loadedFiles: string[]): boolean =>
  isPOIIfcLoadedDeep(poi, loadedFiles);

export const isPOILoading = (poi: any, loadingFiles: string[]): boolean =>
  isPOIIfcLoadingDeep(poi, loadingFiles);

// ============================================================================
// Context Types
// ============================================================================

interface POITableContextValue {
  // Current site
  siteId: string | null;
  setSiteId: (id: string | null) => void;

  // POI data
  pois: any[];
  loading: boolean;
  error: string | null;
  /**
   * Reload the site's assets. `quiet` keeps the table on screen while it loads (a change made
   * elsewhere, e.g. the viewer creating an asset); without it the table shows its loading state.
   */
  refreshPOIs: (options?: { quiet?: boolean }) => Promise<void>;

  assetChoices: AssetFilterChoices;
  setAssetChoices: React.Dispatch<React.SetStateAction<AssetFilterChoices>>;
  facetReader: AssetFacetReader;
  unfacetedPOIs: any[];
  searchedPOIs: any[];
  // Search & Filter state
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filterGroups: FilterGroup[];
  setFilterGroups: (groups: FilterGroup[]) => void;
  showFilterPanel: boolean;
  setShowFilterPanel: (show: boolean) => void;

  // Column configuration
  columns: ColumnConfig[];
  setColumns: React.Dispatch<React.SetStateAction<ColumnConfig[]>>;
  showColumnSelector: boolean;
  setShowColumnSelector: (show: boolean) => void;

  // Sorting and pagination
  sortConfig: SortConfig;
  setSortConfig: (config: SortConfig) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;

  // Selection state
  selectedPOIs: Set<string>;
  setSelectedPOIs: (selection: Set<string>) => void;
  expandedPOI: string | null;
  setExpandedPOI: (id: string | null) => void;
  showBulkActions: boolean;
  setShowBulkActions: (show: boolean) => void;

  // Edit mode state
  isEditMode: boolean;
  setIsEditMode: (editing: boolean) => void;
  editedPOIs: Record<string, any>;
  setEditedPOIs: (edits: Record<string, any>) => void;
  modifiedPOIs: Set<number>;
  setModifiedPOIs: (modified: Set<number>) => void;
  savingPOIs: boolean;
  setSavingPOIs: (saving: boolean) => void;

  // Available rooms for editing
  availableRooms: RoomOption[];

  // Computed values
  assignmentStats: RoomAssignmentStats;
  filteredPOIs: any[];
  paginatedPOIs: any[];
  totalPages: number;
  fieldOptions: Record<string, string[]>;
  dynamicColumns: ColumnConfig[];
  allAvailableColumns: ColumnConfig[];

  // Actions
  handleSort: (field: string) => void;
  handleSelectAll: (rows?: any[]) => void;
  handleSelectPOI: (poiId: string) => void;
  resetState: () => void;
}

const POITableContext = createContext<POITableContextValue | undefined>(undefined);

// ============================================================================
// Provider Component
// ============================================================================

interface POITableProviderProps {
  children: ReactNode;
}

export function POITableProvider({ children }: POITableProviderProps) {
  // Site ID - when this changes, all state resets
  const [siteId, setSiteIdInternal] = useState<string | null>(null);

  // POI data
  const [pois, setPois] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search & Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [assetChoices, setAssetChoices] = useState<AssetFilterChoices>({});
  const [facetLocations, setFacetLocations] = useState<any[]>([]);
  const facetReader = useMemo(() => createAssetFacetReader(facetLocations), [facetLocations]);
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Column configuration
  const [columns, setColumns] = useState<ColumnConfig[]>(DEFAULT_COLUMNS);
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Sorting and pagination
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  // Selection state
  const [selectedPOIs, setSelectedPOIs] = useState<Set<string>>(new Set());
  const [expandedPOI, setExpandedPOI] = useState<string | null>(null);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedPOIs, setEditedPOIs] = useState<Record<string, any>>({});
  const [modifiedPOIs, setModifiedPOIs] = useState<Set<number>>(new Set());
  const [savingPOIs, setSavingPOIs] = useState(false);

  // Available rooms
  const [availableRooms, setAvailableRooms] = useState<RoomOption[]>([]);

  // Reset all state (called when site changes)
  const resetState = useCallback(() => {
    setPois([]);
    setSearchTerm('');
    setFilterGroups([]);
    setAssetChoices({});
    setFacetLocations([]);
    setShowFilterPanel(false);
    setColumns(DEFAULT_COLUMNS);
    setShowColumnSelector(false);
    setSortConfig({ field: 'name', direction: 'asc' });
    setCurrentPage(1);
    setSelectedPOIs(new Set());
    setExpandedPOI(null);
    setShowBulkActions(false);
    setIsEditMode(false);
    setEditedPOIs({});
    setModifiedPOIs(new Set());
    setSavingPOIs(false);
    setAvailableRooms([]);
    setError(null);
  }, []);

  // Set site ID with state load/reset
  const setSiteId = useCallback((id: string | null) => {
    if (id !== siteId) {
      if (id) {
        // Try to load persisted state for new site
        const persisted = loadPersistedState(id);
        if (persisted) {
          // Restore persisted state
          setPois([]);  // POIs will be fetched fresh
          setSelectedPOIs(new Set(persisted.selectedPOIs || []));
          setSearchTerm(persisted.searchTerm || '');
          setFilterGroups(persisted.filterGroups || []);
          setAssetChoices(normalizeAssetChoices(persisted.assetChoices));
          setFacetLocations([]);
          setSortConfig(persisted.sortConfig || { field: 'name', direction: 'asc' });
          setColumns(persisted.columns || DEFAULT_COLUMNS);
          // Reset transient state
          setShowFilterPanel(false);
          setShowColumnSelector(false);
          setCurrentPage(1);
          setExpandedPOI(null);
          setShowBulkActions(false);
          setIsEditMode(false);
          setEditedPOIs({});
          setModifiedPOIs(new Set());
          setSavingPOIs(false);
          setAvailableRooms([]);
          setError(null);
        } else {
          // Fresh state for new site (no persisted state)
          resetState();
        }
      } else {
        resetState();
      }
      setSiteIdInternal(id);
    }
  }, [siteId, resetState]);

  // Fetch POIs when site changes
  const refreshPOIs = useCallback(async (options?: { quiet?: boolean }) => {
    if (!siteId) {
      setPois([]);
      return;
    }
    const quiet = options?.quiet === true;

    try {
      if (!quiet) setLoading(true);
      setError(null);

      // Use apiService.get() for consistent auth handling (fixes missing auth header issue)
      const data = await apiService.get(`/assets/${siteId}/pois`);
      const poisArray = Array.isArray(data) ? data : (data.pois || []);

      // Aggregate POIs by ID - API returns multiple rows for POIs in multiple rooms
      // (canonical implementation; this context previously carried a drifted inline copy)
      setPois(aggregatePOIRows(poisArray));
    } catch (err) {
      // A quiet reload keeps the list it has: an error screen in place of a table that was fine
      // a moment ago would be worse than a list one asset behind.
      if (!quiet) setError(err instanceof Error ? err.message : 'Failed to load POIs');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [siteId]);

  // Fetch POIs when site changes
  useEffect(() => {
    if (siteId) {
      refreshPOIs();
    }
  }, [siteId, refreshPOIs]);

  // Fetch available rooms and assignment ancestry when site changes.
  useEffect(() => {
    let current = true;
    const fetchRooms = async () => {
      if (!siteId) {
        setAvailableRooms([]);
        return;
      }

      try {
        const response = await apiService.get(`/assets/${siteId}/locations`);
        if (!current) return;
        const locations = Array.isArray(response) ? response : [];
        setFacetLocations(locations);

        // Build storey map for level names
        const storeyMap = new Map<string, string>();
        locations.forEach((loc: any) => {
          const locationType = loc.type || loc.location_type;
          if (locationType === 'Building Storey') {
            storeyMap.set(loc.id, loc.name);
          }
        });

        // Accept both the legacy and normalized location payload shapes.
        const rooms = locations
          .filter((loc: any) => {
            const locationType = loc.type || loc.location_type;
            return locationType === 'Spaces';
          })
          .map((loc: any) => ({
            id: loc.id,
            name: loc.name,
            level_name:
              loc.parent_name ||
              loc.level_name ||
              (loc.parent_id ? storeyMap.get(loc.parent_id) : undefined)
          }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name));

        setAvailableRooms(rooms);
      } catch (error) {
        if (!current) return;
        console.error('Failed to fetch rooms:', error);
        setAvailableRooms([]);
      }
    };

    fetchRooms();
    return () => { current = false; };
  }, [siteId]);

  // Persist state to sessionStorage when key values change
  const isInitialMount = useRef(true);
  useEffect(() => {
    // Skip saving on initial mount to avoid overwriting persisted state
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (!siteId) return;

    const state: PersistedState = {
      assetChoices,
      selectedPOIs: Array.from(selectedPOIs),
      searchTerm,
      filterGroups,
      sortConfig,
      columns
    };

    savePersistedState(siteId, state);
  }, [siteId, selectedPOIs, searchTerm, filterGroups, sortConfig, columns, assetChoices]);

  // Pre-computed search index: lowercase concatenation of searchable fields per POI.
  // Built once when pois change, avoids 24+ string ops per POI on every search keystroke.
  const searchIndex = useMemo(() => {
    return pois.map(poi => {
      const fields = [
        getFieldValue(poi, 'name'), getFieldValue(poi, 'category'),
        getFieldValue(poi, 'type'), getFieldValue(poi, 'subcategory'),
        getFieldValue(poi, 'room_name'), getFieldValue(poi, 'room_number'),
        getFieldValue(poi, 'level'), getFieldValue(poi, 'workset'),
        getFieldValue(poi, 'system_category'), getFieldValue(poi, 'source_file'),
        getFieldValue(poi, 'element_id'), getFieldValue(poi, 'mark'),
      ].filter(Boolean).join(' ').toLowerCase();
      return fields;
    });
  }, [pois]);

  // Filtered POIs
  // Room-assignment coverage — drives the "POIs not assigned to rooms" indicator on
  // every table surface (a fresh RVT site is storey-only until Reassign is run).
  const assignmentStats = useMemo(() => computeRoomAssignmentStats(pois), [pois]);

  const searchedPOIs = useMemo(() => {
    let result = pois;

    // Apply text search using pre-computed index
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((poi, index) => {
        // Fast check against pre-computed index (covers 12 predefined fields)
        if (searchIndex[index].includes(term)) {
          return true;
        }

        // Fallback: search entire custom_data JSON
        if (poi.custom_data) {
          const customDataStr = typeof poi.custom_data === 'string'
            ? poi.custom_data.toLowerCase()
            : JSON.stringify(poi.custom_data).toLowerCase();
          if (customDataStr.includes(term)) {
            return true;
          }
        }

        return false;
      });
    }

    // Apply sorting
    result = [...result].sort((a, b) => {
      const aVal = String(getFieldValue(a, sortConfig.field) || '').toLowerCase();
      const bVal = String(getFieldValue(b, sortConfig.field) || '').toLowerCase();
      const comparison = aVal.localeCompare(bVal);
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [pois, searchTerm, searchIndex, sortConfig]);

  const unfacetedPOIs = useMemo(() => searchedPOIs.filter(poi => matchesFilterGroups(poi, filterGroups)), [searchedPOIs, filterGroups]);

  const filteredPOIs = useMemo(() => unfacetedPOIs.filter(poi => matchesAssetChoices(poi, assetChoices, facetReader)), [unfacetedPOIs, assetChoices, facetReader]);

  // Paginated POIs
  const paginatedPOIs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredPOIs.slice(start, start + pageSize);
  }, [filteredPOIs, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredPOIs.length / pageSize);

  // Dynamic field options for select filters
  const fieldOptions = useMemo(() => {
    const options: Record<string, string[]> = {};
    const selectFields = ['category', 'type', 'subcategory', 'room_name', 'source_file',
                          'workset', 'phase', 'level', 'system_category', 'system_subcategory', 'system_name'];

    selectFields.forEach(field => {
      const values = new Set<string>();
      pois.forEach(poi => {
        const val = getFieldValue(poi, field);
        if (val) values.add(val);
      });
      options[field] = Array.from(values).sort();
    });
    return options;
  }, [pois]);

  // Dynamic columns from custom_data
  const dynamicColumns = useMemo((): ColumnConfig[] => {
    const allKeys = new Set<string>();

    pois.forEach(poi => {
      const customData = parseCustomData(poi);
      if (!customData) return;

      // Extract RVT properties
      const rvtProps = customData?.rvt?.rvtProperties || {};
      Object.keys(rvtProps).forEach(key => allKeys.add(`rvt:${key}`));

      // Extract IFC properties
      const ifcProps = customData?.ifc?.properties || {};
      Object.keys(ifcProps).forEach(key => allKeys.add(`ifc:${key}`));

      // Extract IFC property sets
      const ifcPropSets = customData?.ifc?.propertySets || {};
      Object.entries(ifcPropSets).forEach(([setName, props]) => {
        if (typeof props === 'object' && props !== null) {
          Object.keys(props as object).forEach(key => allKeys.add(`ifc:${setName}.${key}`));
        }
      });
    });

    return Array.from(allKeys).sort().map(key => {
      const propName = key.replace(/^(rvt|ifc):/, '');
      return {
        field: key,
        label: propName,
        visible: propName.startsWith('IAA') || propName === 'Elevation from Level' || propName === 'System Type',
        width: 150
      };
    });
  }, [pois]);

  // All available columns (static + dynamic)
  const allAvailableColumns = useMemo(() => {
    const staticFields = new Set(DEFAULT_COLUMNS.map(c => c.field));
    const uniqueDynamic = dynamicColumns.filter(dc => !staticFields.has(dc.field));
    return [...DEFAULT_COLUMNS, ...uniqueDynamic];
  }, [dynamicColumns]);

  // Retain chosen model columns/widths through refresh; add newly discovered defaults.
  useEffect(() => {
    if (dynamicColumns.length === 0) return;
    setColumns(prev => {
      const known = new Set(prev.map(column => column.field));
      return [...prev, ...dynamicColumns.filter(column => column.visible && !known.has(column.field))];
    });
  }, [dynamicColumns]);

  // Handle sort
  const handleSort = useCallback((field: string) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  }, []);

  // Handle select all
  const handleSelectAll = useCallback((rows = paginatedPOIs) => {
    setSelectedPOIs(previous => toggleAssetPageSelection(previous, rows.map(poi => String(poi.id))));
  }, [paginatedPOIs]);

  // Handle select POI
  const handleSelectPOI = useCallback((poiId: string) => {
    setSelectedPOIs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(poiId)) {
        newSet.delete(poiId);
      } else {
        newSet.add(poiId);
      }
      return newSet;
    });
  }, []);

  const value: POITableContextValue = {
    // Site
    siteId,
    setSiteId,

    // POI data
    pois,
    loading,
    error,
    refreshPOIs,

    // Search & Filter
    searchTerm,
    setSearchTerm,
    assetChoices, setAssetChoices, facetReader, unfacetedPOIs, searchedPOIs,
    filterGroups,
    setFilterGroups,
    showFilterPanel,
    setShowFilterPanel,

    // Columns
    columns,
    setColumns,
    showColumnSelector,
    setShowColumnSelector,

    // Sorting & Pagination
    sortConfig,
    setSortConfig,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,

    // Selection
    selectedPOIs,
    setSelectedPOIs,
    expandedPOI,
    setExpandedPOI,
    showBulkActions,
    setShowBulkActions,

    // Edit mode
    isEditMode,
    setIsEditMode,
    editedPOIs,
    setEditedPOIs,
    modifiedPOIs,
    setModifiedPOIs,
    savingPOIs,
    setSavingPOIs,

    // Rooms
    availableRooms,

    // Computed
    assignmentStats,
    filteredPOIs,
    paginatedPOIs,
    totalPages,
    fieldOptions,
    dynamicColumns,
    allAvailableColumns,

    // Actions
    handleSort,
    handleSelectAll,
    handleSelectPOI,
    resetState
  };

  return (
    <POITableContext.Provider value={value}>
      {children}
    </POITableContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function usePOITable(): POITableContextValue {
  const context = useContext(POITableContext);
  if (context === undefined) {
    throw new Error('usePOITable must be used within a POITableProvider');
  }
  return context;
}

export default POITableContext;
